import { useSelector } from 'react-redux';
import { RootState } from '../store';

/**
 * Custom hook to access authentication state
 * Provides easy access to auth state from Redux store
 * Requirements: 1.1, 1.7
 */
export const useAuth = () => {
  const auth = useSelector((state: RootState) => state.auth);

  return {
    isAuthenticated: auth.isAuthenticated,
    user: auth.user,
    token: auth.token,
    currentWorkspace: auth.currentWorkspace,
    workspaces: auth.workspaces,
  };
};

/**
 * Hook to check if user has specific role in current workspace
 * @param requiredRole - Role to check for
 * @returns True if user has the required role
 */
export const useHasRole = (requiredRole: 'admin' | 'manager' | 'viewer'): boolean => {
  const { user, currentWorkspace } = useAuth();

  // Superadmins have all roles
  if (user?.role === 'superadmin') {
    return true;
  }

  if (!currentWorkspace) {
    return false;
  }

  const roleHierarchy = {
    admin: 3,
    manager: 2,
    viewer: 1,
  };

  const userRoleLevel = roleHierarchy[currentWorkspace.role as keyof typeof roleHierarchy] || 0;
  const requiredRoleLevel = roleHierarchy[requiredRole];

  return userRoleLevel >= requiredRoleLevel;
};

/**
 * Hook to check if user has permission for specific action
 * @param action - Action to check permission for
 * @returns True if user has permission
 */
export const useHasPermission = (action: string): boolean => {
  const { user, currentWorkspace } = useAuth();

  // Superadmins have all permissions
  if (user?.role === 'superadmin') {
    return true;
  }

  if (!currentWorkspace) {
    return false;
  }

  const role = currentWorkspace.role;

  // Permission matrix based on requirements
  const permissions: Record<string, string[]> = {
    // Admin permissions
    'users.create': ['admin'],
    'users.update': ['admin'],
    'users.delete': ['admin'],
    'workspace.update': ['admin'],
    'workspace.delete': ['admin'],

    // Manager permissions
    'content.create': ['admin', 'manager'],
    'content.update': ['admin', 'manager'],
    'content.delete': ['admin', 'manager'],
    'playlist.create': ['admin', 'manager'],
    'playlist.update': ['admin', 'manager'],
    'playlist.delete': ['admin', 'manager'],
    'layout.create': ['admin', 'manager'],
    'layout.update': ['admin', 'manager'],
    'layout.delete': ['admin', 'manager'],
    'display.create': ['admin', 'manager'],
    'display.update': ['admin', 'manager'],
    'display.delete': ['admin', 'manager'],
    'schedule.create': ['admin', 'manager'],
    'schedule.update': ['admin', 'manager'],
    'schedule.delete': ['admin', 'manager'],
    'emergency.activate': ['admin', 'manager'],

    // Viewer permissions (read-only)
    'content.view': ['admin', 'manager', 'viewer'],
    'playlist.view': ['admin', 'manager', 'viewer'],
    'layout.view': ['admin', 'manager', 'viewer'],
    'display.view': ['admin', 'manager', 'viewer'],
    'schedule.view': ['admin', 'manager', 'viewer'],
    'analytics.view': ['admin', 'manager', 'viewer'],
  };

  const allowedRoles = permissions[action] || [];
  return allowedRoles.includes(role);
};
